import { Button } from '@components'
import { NocfoPlan } from '@constants'
import {
  CardWrapper,
  ContentCol,
  HeaderRow,
  IconCol,
  StyledCard,
  UnstyledButton
} from '@root/containers/Stripe/components/SubscriptionRowCard'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaDotCircle } from 'react-icons/fa'
import { FaRegCircle } from 'react-icons/fa6'
import styled from 'styled-components'
import { BillingInterval } from '@query'
import { ManualInvoicingForm } from './ManualInvoicingForm'

type BillingSetupMode = 'plan-order' | 'einvoicing' | 'payment-method'

const MANUAL_CARD_ID = 'manual'

interface CardOption {
  id: string
  kind: 'stripe' | 'manual'
  titleKey: string
  descriptionKey: string
}

const PLAN_ORDER_CARDS: CardOption[] = [
  {
    id: 'card',
    kind: 'stripe',
    titleKey: 'components.billingSetup.cards.addCard.title',
    descriptionKey: 'components.billingSetup.cards.addCard.description'
  },
  {
    id: MANUAL_CARD_ID,
    kind: 'manual',
    titleKey: 'components.billingSetup.cards.manualPlan.title',
    descriptionKey: 'components.billingSetup.cards.manualPlan.description'
  }
]

const EINVOICING_CARDS: CardOption[] = [
  {
    id: 'card',
    kind: 'stripe',
    titleKey: 'components.billingSetup.cards.updatePaymentMethod.title',
    descriptionKey: 'components.billingSetup.cards.updatePaymentMethod.description'
  },
  {
    id: MANUAL_CARD_ID,
    kind: 'manual',
    titleKey: 'components.billingSetup.cards.manualPlan.title',
    descriptionKey: 'components.billingSetup.cards.manualPlan.description'
  }
]

const resolveInitialCardId = (cardId: string | null, cards: CardOption[]): string | null => {
  if (cardId === 'email' || cardId === 'einvoice' || cardId === MANUAL_CARD_ID) {
    return MANUAL_CARD_ID
  }
  if (cardId && cards.some(card => card.id === cardId)) {
    return cardId
  }
  return null
}

interface Props {
  mode: BillingSetupMode
  /** Plan-order flow: plan chosen in the pricing table (read-only in the form). */
  selectedPlan?: NocfoPlan
  /** Plan-order flow: interval chosen in the pricing table (read-only in the form). */
  selectedBillingInterval?: BillingInterval
  initialSelectedCardId?: string | null
  /** Stripe path: plan-order triggers checkout, e-invoicing triggers payment setup. */
  onStripeAction: () => void
  isStripeActionPending?: boolean
  onManualSubmitSuccess?: () => void
}

export const BillingSetupPanel: React.FC<Props> = ({
  mode,
  selectedPlan,
  selectedBillingInterval,
  initialSelectedCardId = null,
  onStripeAction,
  isStripeActionPending = false,
  onManualSubmitSuccess
}) => {
  const [t] = useTranslation()
  const cards =
    mode === 'plan-order'
      ? PLAN_ORDER_CARDS
      : mode === 'payment-method'
      ? EINVOICING_CARDS.filter(card => card.kind === 'stripe')
      : EINVOICING_CARDS
  const [selectedCardId, setSelectedCardId] = useState<string | null>(() =>
    resolveInitialCardId(initialSelectedCardId, cards)
  )

  useEffect(() => {
    setSelectedCardId(resolveInitialCardId(initialSelectedCardId, cards))
  }, [initialSelectedCardId, cards])

  const handleSelectCard = (cardId: string) => {
    setSelectedCardId(cardId)
  }

  return (
    <Wrapper>
      <CardWrapper>
        {cards.map(card => {
          const isActive = card.id === selectedCardId
          return (
            <CardShell key={card.id} $isActive={isActive} $isExpanded={isActive}>
              <UnstyledButton type="button" onClick={() => handleSelectCard(card.id)}>
                <OptionCard $isActive={isActive} $isExpanded={isActive}>
                  <BillingIconCol $isActive={isActive}>
                    {isActive ? <FaDotCircle /> : <FaRegCircle />}
                  </BillingIconCol>
                  <ContentCol>
                    <HeaderRow>
                      <h4>{t(card.titleKey)}</h4>
                    </HeaderRow>
                    <Description>{t(card.descriptionKey)}</Description>
                  </ContentCol>
                </OptionCard>
              </UnstyledButton>

              <AnimatePresence initial={false}>
                {isActive && (
                  <ExpandedPanel
                    key={card.id}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                  >
                    <ExpandedInner>
                      {card.kind === 'stripe' && (
                        <ActionRow>
                          <Button
                            type="button"
                            intent="primary"
                            onClick={onStripeAction}
                            showSpinner={isStripeActionPending}
                            disabled={isStripeActionPending}
                          >
                            {t('components.billingSetup.continue')}
                          </Button>
                        </ActionRow>
                      )}

                      {card.kind === 'manual' && (
                        <ManualInvoicingForm
                          fixedPlan={mode === 'plan-order' ? selectedPlan : undefined}
                          fixedBillingInterval={
                            mode === 'plan-order' ? selectedBillingInterval : undefined
                          }
                          onSubmitSuccess={onManualSubmitSuccess}
                        />
                      )}
                    </ExpandedInner>
                  </ExpandedPanel>
                )}
              </AnimatePresence>
            </CardShell>
          )
        })}
      </CardWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
`

const OptionCard = styled(StyledCard)<{ $isExpanded: boolean }>`
  background: ${({ theme }) => theme.colors.neutralWhite};

  ${({ $isExpanded, $isActive, theme }) =>
    $isExpanded
      ? `
    border: none;
    border-radius: 0;
  `
      : `
    border: 2px solid
      ${$isActive ? theme.colors.primary : theme.colors.neutralGray};
  `}
`

const BillingIconCol = styled(IconCol)`
  background: ${({ theme }) => theme.colors.defaultHover};
`

const CardShell = styled.div<{ $isActive: boolean; $isExpanded: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutralWhite};
  ${({ $isExpanded, $isActive, theme }) =>
    $isExpanded &&
    $isActive &&
    `
    border: 2px solid ${theme.colors.primary};
  `}
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const ExpandedPanel = styled(motion.div)`
  overflow: hidden;
`

const ExpandedInner = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.md}rem;
`

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`
